import { useEffect, useState } from 'react';
import { create } from 'zustand';
import { useQueryClient } from '@tanstack/react-query';
import type { Avatar as AvatarT, DateLength, DateSeries } from '@/api/types';
import { endpoints } from '@/api';
import { errorMessage } from '@/api/errors';
import { qk } from '@/api/queries';
import { toast } from '@/stores/toasts';
import { Sheet } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Avatar } from '@/components/ui/Avatar';
import { SelectField } from '@/components/ui/Field';
import { cn } from '@/lib/cn';
import { DAYS, browserZone, clockLabel } from '@/features/links/links';
import { seriesLabel, seriesTimes } from './series';

export interface SeriesPerson {
  id: string;
  display_name: string;
  avatar?: AvatarT;
}

interface SeriesComposer {
  person: SeriesPerson | null;
  /** Prefill from a date you just had (its weekday and time). */
  from: Date | null;
  start: (person: SeriesPerson, from?: Date | null) => void;
  close: () => void;
}

/** "Make it a weekly thing" opens from a friend's menu or after a date (SPEC_V9 §A5). */
export const useSeriesComposer = create<SeriesComposer>((set) => ({
  person: null,
  from: null,
  start: (person, from = null) => set({ person, from }),
  close: () => set({ person: null, from: null }),
}));

function roundedTime(d: Date): string {
  const m = Math.min(23 * 60 + 30, Math.max(7 * 60, Math.round((d.getHours() * 60 + d.getMinutes()) / 30) * 30));
  return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
}

/** Weekday, time and length → POST /v1/date-series (pending until they accept). */
export function SeriesSheet() {
  const { person, from, close } = useSeriesComposer();
  const qc = useQueryClient();
  const [weekday, setWeekday] = useState(4);
  const [time, setTime] = useState('21:00');
  const [length, setLength] = useState<DateLength>(30);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!person) return;
    setWeekday(from ? from.getDay() : 4);
    setTime(from ? roundedTime(from) : '21:00');
    setLength(30);
    setErr(null);
  }, [person, from]);

  const propose = async () => {
    if (!person || busy) return;
    setBusy(true);
    setErr(null);
    try {
      const s = await endpoints.proposeSeries({ to_user_id: person.id, weekday, time, tz: browserZone(), length_min: length });
      qc.setQueryData<{ series: DateSeries[] }>(qk.dateSeries, (p) => (p ? { series: [s, ...p.series.filter((x) => x.id !== s.id)] } : p));
      void qc.invalidateQueries({ queryKey: qk.dateSeries });
      toast.success('Asked!', `${seriesLabel(s)}. Once ${person.display_name} says yes, we set up each week for you.`);
      close();
    } catch (e) {
      setErr(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet open={!!person} onClose={close} title="Make it a weekly thing" description="A standing date night. Skip a week, pause or stop any time." testId="series-sheet">
      {person && (
        <div className="space-y-5">
          <div className="flex items-center gap-3">
            <Avatar avatar={person.avatar} size={44} label={person.display_name} />
            <p className="text-[16px] font-semibold">With {person.display_name}</p>
          </div>
          <div>
            <p className="mb-2 text-[15px] font-semibold">Which day?</p>
            <div role="radiogroup" aria-label="Day of the week" className="grid grid-cols-7 gap-1">
              {DAYS.map((d, i) => (
                <button
                  key={d}
                  type="button"
                  role="radio"
                  aria-checked={weekday === i}
                  onClick={() => setWeekday(i)}
                  className={cn('h-11 rounded-full text-[13px] font-semibold transition-colors duration-150 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-fg', weekday === i ? 'bg-primary text-ground' : 'bg-raised text-muted hover:text-fg')}
                  data-testid={`series-day-${i}`}
                >
                  {d}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <SelectField label="Time" value={time} onChange={(e) => setTime(e.target.value)} data-testid="series-time">
              {seriesTimes().map((t) => (
                <option key={t} value={t}>
                  {clockLabel(t)}
                </option>
              ))}
            </SelectField>
            <div>
              <p className="mb-2 text-[15px] font-semibold">Length</p>
              <div role="radiogroup" aria-label="Length" className="grid grid-cols-2 gap-1 rounded-full bg-raised p-1">
                {([30, 60] as const).map((l) => (
                  <button
                    key={l}
                    type="button"
                    role="radio"
                    aria-checked={length === l}
                    onClick={() => setLength(l)}
                    className={cn('h-9 rounded-full text-sm font-semibold tabular-nums transition-colors duration-150', length === l ? 'bg-primary text-ground' : 'text-muted hover:text-fg')}
                    data-testid={`series-length-${l}`}
                  >
                    {l} min
                  </button>
                ))}
              </div>
            </div>
          </div>
          <p className="rounded-card bg-yellow p-4 text-[17px] font-semibold text-night" data-testid="series-preview">
            {seriesLabel({ weekday, time })} · {length} min
          </p>
          {err && (
            <p role="alert" className="text-sm font-medium text-danger" data-testid="series-error">
              {err}
            </p>
          )}
          <Button block size="lg" icon="heart" onClick={() => void propose()} loading={busy} data-testid="series-propose">
            Ask {person.display_name}
          </Button>
          <p className="text-[13px] text-muted">Each week’s date is set up 6 days ahead, with the planner, reminders and everything. You just show up.</p>
        </div>
      )}
    </Sheet>
  );
}
